import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
  type ChangeEvent,
} from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Check as FeatherCheck } from 'react-feather'
import { Icon } from '@/components'
import BackButton from '@/components/BackButton/BackButton'
import { PrivacyTerms } from './ProgramSignupSheetContent'

const CODE_LENGTH = 6
// Mirrors users/constants.py:OTP_CODE_TTL_SECONDS on the BE.
const CODE_TTL_SECONDS = 600

export type OtpCodeEntryProps = {
  email: string
  /** Title above the boxes. Differs between login ("Verify and Log in") and signup. */
  title?: string
  /** Submit button label. */
  submitLabel: string
  /** Whether the OTP was requested from the login flow. Controls subtitle copy. */
  isLogin?: boolean
  loading: boolean
  /** True while the OTP *request* (send / resend) is in-flight. */
  requestLoading: boolean
  resendLoading: boolean
  /** When non-empty, shown above the boxes as a red error banner. */
  errorMessage: string | null
  /** When true, shows the green "new code sent" banner. */
  resendConfirmationVisible: boolean
  onSubmit: (code: string) => void
  onResend: () => void
  onBack: () => void
  /** Resets the input boxes — used after a verify failure to clear stale digits. */
  resetSignal?: number
}

/**
 * Six-box OTP entry per Figma. Reused by login + signup flows.
 *
 * Behavior:
 * - Digits-only, auto-advance + backspace-to-prev
 * - Pasting a 6-digit string fills all boxes at once and triggers submit
 * - Countdown timer counts down from CODE_TTL_SECONDS
 * - Verify button disabled until all 6 boxes are filled
 */
export default function OtpCodeEntry({
  email,
  title = 'Enter your verification code',
  submitLabel,
  isLogin = false,
  loading,
  requestLoading,
  resendLoading,
  errorMessage,
  resendConfirmationVisible,
  onSubmit,
  onResend,
  onBack,
  resetSignal = 0,
}: OtpCodeEntryProps) {
  const [digits, setDigits] = useState<string[]>(() =>
    Array(CODE_LENGTH).fill(''),
  )
  const [secondsRemaining, setSecondsRemaining] =
    useState<number>(CODE_TTL_SECONDS)
  /** Local error shown when the user submits an incomplete code. Cleared on
   *  any digit edit and on parent reset. */
  const [incompleteError, setIncompleteError] = useState<string | null>(null)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Reset boxes whenever the parent bumps `resetSignal` (e.g. after a failed
  // verify or a fresh resend).
  useEffect(
    function resetOnSignal() {
      setDigits(Array(CODE_LENGTH).fill(''))
      setIncompleteError(null)
      inputRefs.current[0]?.focus()
    },
    [resetSignal],
  )

  // Restart the countdown when a new code is sent.
  useEffect(
    function restartCountdownOnResend() {
      setSecondsRemaining(CODE_TTL_SECONDS)
    },
    [resendConfirmationVisible],
  )

  useEffect(function tickCountdown() {
    const interval = setInterval(() => {
      setSecondsRemaining((s) => (s > 0 ? s - 1 : 0))
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  const code = useMemo(() => digits.join(''), [digits])
  const isComplete =
    code.length === CODE_LENGTH && digits.every((d) => d !== '')

  const handleChange = useCallback(
    (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
      // Any edit clears the incomplete-submit error so it doesn't follow the
      // user as they correct it.
      setIncompleteError(null)
      const raw = e.target.value
      // If the user pasted multiple digits into a single box, route through
      // the same paste-fill logic instead.
      if (raw.length > 1) {
        const fill = raw.replace(/\D/g, '').slice(0, CODE_LENGTH).split('')
        setDigits((prev) => {
          const next = [...prev]
          for (let i = 0; i < fill.length && index + i < CODE_LENGTH; i++) {
            next[index + i] = fill[i]
          }
          return next
        })
        const lastFilled = Math.min(index + fill.length, CODE_LENGTH - 1)
        inputRefs.current[lastFilled]?.focus()
        return
      }
      const digit = raw.replace(/\D/g, '')
      setDigits((prev) => {
        const next = [...prev]
        next[index] = digit
        return next
      })
      if (digit && index < CODE_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus()
      }
    },
    [],
  )

  const handleKeyDown = useCallback(
    (index: number) => (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !digits[index] && index > 0) {
        // Empty box → jump back and let the next render clear that one.
        inputRefs.current[index - 1]?.focus()
      } else if (e.key === 'ArrowLeft' && index > 0) {
        inputRefs.current[index - 1]?.focus()
      } else if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus()
      }
    },
    [digits],
  )

  const handlePaste = useCallback((e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, CODE_LENGTH)
    if (!text) return
    e.preventDefault()
    const next = Array(CODE_LENGTH).fill('')
    for (let i = 0; i < text.length; i++) next[i] = text[i]
    setDigits(next)
    const lastIdx = Math.min(text.length, CODE_LENGTH) - 1
    inputRefs.current[lastIdx]?.focus()
  }, [])

  const handleSubmit = useCallback(() => {
    if (loading) return
    if (!isComplete) {
      setIncompleteError('Code must be 6 numbers.')
      return
    }
    setIncompleteError(null)
    onSubmit(code)
  }, [code, isComplete, loading, onSubmit])

  const handleFormSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      handleSubmit()
    },
    [handleSubmit],
  )

  const countdown = formatCountdown(secondsRemaining)
  const expired = secondsRemaining === 0
  // When the local TTL has elapsed, show the expired error in the red slot
  // regardless of any other error state (the BE response is ambiguous between
  // "wrong code" and "expired code", so we fall back to the local timer).
  const expiredError = expired
    ? 'Your code expired, resend your code and try again.'
    : null
  const displayedError = expiredError ?? errorMessage ?? incompleteError

  return (
    <div className="flex flex-col gap-6 font-[inter] w-full max-w-[420px] mx-auto box-border">
      <div className="flex flex-col gap-6 w-full">
        <div className="self-start">
          <BackButton onClick={onBack} />
        </div>
        <div className="flex flex-col gap-2 w-full">
          <h2 className="m-0 font-[inter] text-[20px] font-bold not-italic leading-normal text-[#343434]">
            {title}
          </h2>
          <p className="m-0 text-[14px] font-medium text-[#5d5d5d] leading-5">
            {isLogin
              ? 'If you have an account, we sent a 6-digit code to your email '
              : 'We sent a 6-digit code to your email '}
            <span className="font-bold text-[#343434]">{email}</span>
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-6 w-full">
        <AnimatePresence>
          {resendConfirmationVisible && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="flex items-start gap-2 bg-[#E5FBEC] p-4 rounded-[10px]">
                <FeatherCheck
                  size={20}
                  color="#00A331"
                  className="--icon shrink-0"
                />
                <p className="m-0 font-[inter] text-[14px] font-medium leading-5 text-[#00A331]">
                  New code was sent to {email}, check your inbox.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <form
          action="post"
          noValidate
          onSubmit={handleFormSubmit}
          className="flex flex-col gap-4 w-full"
        >
          <div className="flex flex-col gap-2 w-full">
            <div className="grid grid-cols-6 gap-2 w-full">
              {digits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputRefs.current[index] = el
                  }}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={CODE_LENGTH}
                  value={digit}
                  onChange={handleChange(index)}
                  onKeyDown={handleKeyDown(index)}
                  onPaste={handlePaste}
                  aria-label={`Digit ${index + 1}`}
                  data-testing-id={`otp-digit-${index}`}
                  className={`w-full min-w-0 py-6 px-0 text-center text-[20px] font-bold text-[#5d5d5d] bg-white rounded-[8px] border border-solid outline-none transition-colors font-[inter] ${
                    displayedError
                      ? 'border-[#dd0303]'
                      : 'border-[#e5e5e5] focus:border-[#af4bfb]'
                  }`}
                />
              ))}
            </div>

            <AnimatePresence>
              {displayedError && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <p className="m-0 text-[12px] font-medium text-[#dd0303] leading-5 text-center w-full">
                    {displayedError}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {!expired && (
            <div className="flex items-start gap-2 bg-[#f2f2f2] p-2 rounded-[10px] w-full">
              <Icon name="Info" size={20} color="#5d5d5d" />
              <p className="m-0 text-[14px] font-medium text-[#5d5d5d] leading-5">
                Code expires in <span className="font-bold">{countdown}</span>
              </p>
            </div>
          )}

          <div className="flex flex-col gap-2 mt-2 w-full">
            <button
              type="submit"
              disabled={loading || requestLoading}
              className={SUBMIT_BUTTON_CLASS}
            >
              {requestLoading
                ? 'Sending Code...'
                : loading
                ? 'Verifying...'
                : submitLabel}
            </button>
            <EncryptionBadge />
          </div>
        </form>

        <p className="text-center text-[14px] font-medium text-[#343434] leading-5 m-0">
          Didn&apos;t get a code?{' '}
          <button
            type="button"
            onClick={onResend}
            disabled={resendLoading}
            className="text-[#3768FB] font-bold underline leading-6 bg-transparent border-0 cursor-pointer p-0 text-[14px] font-[inter] disabled:opacity-50"
          >
            {resendLoading ? 'Sending...' : 'Resend code'}
          </button>
        </p>

        <PrivacyTerms />
      </div>
    </div>
  )
}

function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

function EncryptionBadge() {
  return (
    <div className="flex items-center justify-center gap-2">
      <Icon name="Shield" size={16} color="#5d5d5d" />
      <span className="text-[12px] font-medium text-[#5d5d5d] leading-5">
        256-bit encryption
      </span>
    </div>
  )
}

const SUBMIT_BUTTON_CLASS =
  'w-full bg-[#af4bfb] hover:bg-[#9a3ae6] disabled:opacity-50 text-white font-bold text-[14px] leading-5 p-5 rounded-[20px] transition-colors border-0 cursor-pointer font-[inter]'
